import React, { useEffect } from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import {
  Inbox,
  Ship,
  Search,
  Users,
  IdCardLanyard,
  Bot,
  Settings,
  Shield,
  LogOut,
} from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { TooltipProvider } from '@/components/ui/tooltip'
import { toast } from 'sonner'
import { useOrgPath } from '@/lib/pathFor'
import NavIcon, { type NavItem } from '@/components/shared/NavIcon'
import ImpersonationBanner from '@/components/shared/ImpersonationBanner'

const NAV_ITEMS: { name: string; path: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { name: 'Inbox', path: '/inbox', icon: Inbox },
  { name: 'Rate Search', path: '/rate-search', icon: Search },
  { name: 'Shipments', path: '/shipments', icon: Ship },
  { name: 'Clients', path: '/clients', icon: Users },
  { name: 'Agent Activity', path: '/agent_sessions', icon: Bot },
]

export default function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const { url, props } = usePage<{ current_user: { name: string | null; email_address: string; role: string; super_admin: boolean } | null; current_organization?: { slug: string } | null; impersonating_org: { id: string; name: string } | null; email_test_mode?: { override_email: string } | null; flash: { notice?: string; alert?: string } }>()
  const { flash } = props as { flash: { notice?: string; alert?: string } }
  const pathFor = useOrgPath()

  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert)
  }, [flash?.notice, flash?.alert])

  const navItems: NavItem[] = NAV_ITEMS.map(item => ({
    name: item.name,
    href: pathFor(item.path),
    icon: item.icon,
  }))

  function isActive(href: string) {
    if (href === pathFor('/')) return url === pathFor('/')
    return url.startsWith(href)
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen flex-col">
        {/* Impersonation banner */}
        {props.impersonating_org && (
          <ImpersonationBanner orgName={props.impersonating_org.name} />
        )}
        {props.email_test_mode && (
          <div className="flex items-center justify-center gap-2 px-4 py-1.5 text-[13px] font-medium shrink-0 bg-amber-50 border-b border-amber-500 text-amber-800" role="status">
            <span>Email test mode — all outbound email goes to <strong>{props.email_test_mode.override_email}</strong>, not to customers.</span>
            <Link href={pathFor('/settings/automation')} className="underline cursor-pointer">Change</Link>
          </div>
        )}
        <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <nav className="flex w-14 flex-col items-center gap-1 border-r bg-muted/50 py-4">
          {/* Logo */}
          <Link href={pathFor('/')} className="mb-4 block h-8 w-8" aria-label="Home">
            <img src="/nereus-logo.svg" alt="Nereus" className="h-8 w-8 rounded-md" />
          </Link>

          {/* Nav items */}
          {navItems.map((item) => (
            <NavIcon key={item.href} item={item} active={isActive(item.href)} />
          ))}

          {/* Admin nav — super_admin only */}
          {props.current_user?.super_admin && (
            <NavIcon
              item={{ name: 'Admin', href: '/admin', icon: Shield }}
              active={url.startsWith('/admin')}
            />
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Admin-only: Team + Settings icons above user menu */}
          {props.current_user?.role === 'admin' && (
            <>
              <NavIcon
                item={{ name: 'Team', href: pathFor('/settings/users'), icon: IdCardLanyard }}
                active={url.startsWith(pathFor('/settings/users'))}
              />
              <NavIcon
                item={{ name: 'Settings', href: pathFor('/settings'), icon: Settings }}
                active={url.startsWith(pathFor('/settings'))}
              />
            </>
          )}

          {/* User menu popover (bottom) */}
          {props.current_user && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground cursor-pointer"
                >
                  {(props.current_user.name || props.current_user.email_address)
                    .charAt(0)
                    .toUpperCase()}
                </button>
              </PopoverTrigger>
              <PopoverContent side="right" align="end" className="w-48 p-1 gap-1">
                <div className="px-2.5 py-1.5">
                  <p className="text-[13px] font-semibold text-foreground">{props.current_user.name || 'User'}</p>
                  <p className="text-[11px] text-muted-foreground">{props.current_user.email_address}</p>
                  {props.current_user.role === 'admin' && (
                    <p className="text-[10px] font-medium text-primary mt-0.5">Admin</p>
                  )}
                </div>
                <div className="h-px bg-border" />
                <Link href={pathFor('/account')} className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-foreground hover:bg-accent cursor-pointer">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  Account
                </Link>
                {props.current_user.super_admin && (
                  <Link href="/admin" className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-foreground hover:bg-accent cursor-pointer">
                    <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                    Admin
                  </Link>
                )}
                <div className="h-px bg-border" />
                <button
                  onClick={() => router.delete('/session')}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-red-600 hover:bg-red-50 cursor-pointer"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Logout
                </button>
              </PopoverContent>
            </Popover>
          )}
        </nav>

        {/* Main content */}
        <main className="flex flex-1 flex-col overflow-hidden">
          {children}
        </main>
      </div>
      </div>
    </TooltipProvider>
  )
}
